// Edition form template to use with CardTemplate

import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import ImageBtns from './ImageBtns';
import Spinner from './Spinner';
import './CardEditTemplate.scss';

export default function CardEditTemplate({
  children,
  className,
  id,
  imgUrl,
  imgThumbUrl,
  imgPath,
  imgThumbPath,
  // Whether the document has an image at all. Distinct from imgUrl, which for
  // private images only arrives once the bytes do — validating on that would
  // block saving until the download finished, and forever if it failed.
  hasImage = !!(imgUrl || imgPath),
  formConfig,
  create,
  update,
  // A path, or a function of what `create` resolved to
  afterCreateNavPath,
  requireImage = false,
  // Off, the form sends no `image` key at all: some callables refuse it
  withImage = true,
  showUploadWarning,
  placeholder,
  submitText = 'Save',
  // Outside a card's ?edit= there is no view mode to go back to
  onCancel,
  // For a parent that sends the values somewhere before the form is saved
  onValuesChange,
}) {
  const [tmpImg, setTmpImg] = useState('');
  const [tmpImgUrl, setTmpImgUrl] = useState(null);
  const [isImgNew, setIsImgNew] = useState(id === 'new');
  const [spinner, setSpinner] = useState(false);
  const [imgErr, setImgErr] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hash } = useLocation();

  const edit = searchParams.get('edit');

  // The chosen file is already in memory, so the preview comes from a blob URL
  // rather than a read-back of temp/ — the one Storage prefix closed to
  // anonymous reads, and a round trip nobody was waiting for. Held in a ref so
  // the previous blob can be released without the URL entering any deps array.
  const tmpImgUrlRef = useRef(null);

  const setPreview = (url) => {
    if (tmpImgUrlRef.current) URL.revokeObjectURL(tmpImgUrlRef.current);
    tmpImgUrlRef.current = url;
    setTmpImgUrl(url);
  };

  useEffect(
    () => () => {
      if (tmpImgUrlRef.current) URL.revokeObjectURL(tmpImgUrlRef.current);
    },
    [],
  );

  const onSubmit = async (values) => {
    if (imgErr) return;

    try {
      setSpinner(true);
      if (id === 'new') {
        const created = await create(
          withImage ? { ...values, image: tmpImg } : values,
        );
        navigate(
          typeof afterCreateNavPath === 'function'
            ? afterCreateNavPath(created)
            : afterCreateNavPath,
          { replace: true },
        );
      } else {
        await update({
          ...values,
          id,
          ...(withImage && isImgNew ? { image: tmpImg } : {}),
        });
        // keep the hash: setSearchParams would drop it and deselect the tab
        navigate({ search: '?edit=&saved=true', hash }, { replace: true });
      }
      setSpinner(false);
    } catch (error) {
      toast.error(error?.message ?? 'Failed to save');
      setSpinner(false);
    }
  };

  const formik = useFormik({
    ...formConfig,
    onSubmit,
  });

  useEffect(() => {
    if (requireImage && !(isImgNew ? tmpImg : hasImage)) {
      setImgErr('Image required');
    } else setImgErr(null);
  }, [requireImage, isImgNew, tmpImg, hasImage]);

  // Keep latest initialValues without re-triggering the reset effect on every
  // re-render (initialValues is recreated each render by the consumer).
  const initialValuesRef = useRef(formConfig.initialValues);
  initialValuesRef.current = formConfig.initialValues;

  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;
  useEffect(() => {
    onValuesChangeRef.current?.(formik.values);
  }, [formik.values]);

  const { resetForm } = formik;
  useEffect(() => {
    if (edit) {
      resetForm({ values: initialValuesRef.current });
      setIsImgNew(id === 'new');
      setTmpImg('');
      setPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, edit, resetForm]);

  const onUploadImg = (file) => {
    setTmpImg(file.name);
    setPreview(URL.createObjectURL(file));
    setIsImgNew(true);
  };

  const onDeleteImg = () => {
    setTmpImg('');
    setPreview(null);
    setIsImgNew(true);
  };

  const handleSubmit = (e) => {
    if (imgErr) toast.error(imgErr);
    formik.handleSubmit(e);
  };

  const handleCancel =
    onCancel ?? (() => navigate({ search: '?edit=', hash }, { replace: true }));

  return (
    <form
      onSubmit={handleSubmit}
      className={'card-edit-template' + (className ? ' ' + className : '')}
    >
      {withImage && (
        <ImageBtns
          imgUrl={isImgNew ? tmpImgUrl : imgUrl}
          imgThumbUrl={isImgNew ? tmpImgUrl : imgThumbUrl}
          imgPath={isImgNew ? '' : imgPath}
          imgThumbPath={isImgNew ? '' : imgThumbPath}
          imgTitle='Main image'
          size={128}
          onUploadImg={onUploadImg}
          onDeleteImg={onDeleteImg}
          showUploadWarning={showUploadWarning}
          clickable
          placeholder={placeholder}
        />
      )}
      <div className='children-btns'>
        <div className='children-wrap'>{children(formik)}</div>
        <div className='save-cancel-btns'>
          <button className='bw' onClick={handleCancel} type='button'>
            Cancel
          </button>
          <button type='submit'>{submitText}</button>
        </div>
      </div>
      <Spinner show={spinner} />
    </form>
  );
}
